import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { forkJoin, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  SubgroupDeleteDialogComponent,
  SubgroupDeleteDialogData
} from '../fee-subgroup-delete-dialog/fee-subgroup-delete-dialog.component';
import { FeeService } from '../service/fee.service';
import { FeeStateService } from '../service/fee-state.service';
import {
  CreateSubgroupRequest,
  Frequency,
  GroupDetailResponse,
  SubgroupCreateRequest,
  SubgroupResponse,
  UserIndustryResponse
} from '../interface/fee.interface';
import { extendOnlyEndDateValidator, minSelectableStartDate, OVERPAYMENT_EXTEND_ONLY_ERROR, startOfDay, toApiDateString, toFormDateValue } from '../utils/date.utils';
import { SnackBarUtil } from '../qr-copy-message/snack-bar-util.service';
import { MockupDataModel } from '../qr-copy-message/mockup-data.model';
import { TranslateService } from '@ngx-translate/core';
import {
  DEFAULT_PAYMENT_FLOW,
  FEE_FREQUENCY_OPTIONS,
  FEE_PAYMENT_FLOW_OPTIONS,
  isDailySinglePayFrequency,
  resolveSubgroupEndDate
} from '../utils/fee-frequency.utils';
import { PaymentFlow } from '../enums/PaymentFlow';
import { FORM_ERROR, SUBGROUP_FIELD } from '../constants/fee-group-form.constants';

interface SubgroupMeta {
  saved: boolean;
  expanded: boolean;
  minDate: Date;
  memberCount: number;
  extendOnlyFloor: Date | null;
}

@Component({
  selector: 'app-fee-group-create',
  templateUrl: './fee-group-create.component.html',
  styleUrls: ['./fee-group-create.component.scss']
})
export class FeeGroupCreateComponent implements OnInit, OnDestroy {

  private readonly destroy$ = new Subject<void>();

  userIndustryId!: number;
  userIndustry: UserIndustryResponse | null = null;

  groupForm!: FormGroup;
  subgroupMetas: SubgroupMeta[] = [];
  saving = false;
  hasUnsavedChanges = false;
  isEditMode = false;
  isSubgroupEditMode = false;
  editingGroupId: number | null = null;
  editingSubgroupId: number | null = null;
  private originalGroupName = '';
  private returnUrl: string | null = null;

  readonly frequencyOptions = FEE_FREQUENCY_OPTIONS;
  readonly paymentFlowOptions = FEE_PAYMENT_FLOW_OPTIONS;
  readonly subgroupField = SUBGROUP_FIELD;
  readonly formError = FORM_ERROR;

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly feeService: FeeService,
    private readonly feeStateService: FeeStateService,
    private readonly dialog: MatDialog,
    private readonly snackBar: SnackBarUtil,
    private readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.userIndustryId = Number(this.route.snapshot.params['id']);
    const mode = (this.route.snapshot.queryParamMap.get('mode') || '').toLowerCase();
    const queryGroupId = Number(this.route.snapshot.queryParamMap.get('groupId'));
    const querySubgroupId = Number(this.route.snapshot.queryParamMap.get('subgroupId'));
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    this.isSubgroupEditMode = mode === 'edit-subgroup' && !Number.isNaN(querySubgroupId);
    this.editingSubgroupId = this.isSubgroupEditMode ? querySubgroupId : null;
    this.isEditMode = (mode === 'edit' || mode === 'edit-subgroup') && !Number.isNaN(queryGroupId);
    this.editingGroupId = this.isEditMode ? queryGroupId : null;

    this.groupForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(25)]],
      subgroups: this.fb.array([])
    });

    this.groupForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.hasUnsavedChanges = true;
    });

    this.feeService.getUserIndustryById(this.userIndustryId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(ui => {
        this.userIndustry = ui;
        this.feeStateService.setActiveUserIndustry(ui);
      });

    if (this.isEditMode && this.editingGroupId) {
      this.loadGroupForEdit(this.editingGroupId);
    } else {
      this.addSubgroup();
    }
  }

  ngOnDestroy(): void {
    this.feeStateService.setActiveUserIndustry(null);
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges) {
      event.preventDefault();
    }
  }

  get nameCtrl(): AbstractControl {
    return this.groupForm.get('name')!;
  }

  get subgroups(): FormArray {
    return this.groupForm.get('subgroups') as FormArray;
  }

  get atLeastOneSubgroupSaved(): boolean {
    return this.subgroupMetas.some(m => m.saved);
  }

  get canSave(): boolean {
    if (this.saving || this.nameCtrl.invalid) {
      return false;
    }
    return this.subgroups.controls.some((_, i) => this.isSubgroupSavable(i));
  }

  private isSubgroupSavable(index: number): boolean {
    if (this.subgroupMetas[index]?.saved) {
      return true;
    }
    return !this.getSubgroupGroup(index).invalid;
  }

  private markValidSubgroupsAsSaved(): void {
    for (let i = 0; i < this.subgroups.length; i++) {
      const group = this.getSubgroupGroup(i);
      group.markAllAsTouched();
      if (!group.invalid) {
        this.subgroupMetas[i] = { ...this.subgroupMetas[i], saved: true, expanded: false };
      }
    }
  }

  get pageTitle(): string {
    if (this.isSubgroupEditMode) return 'fee.edit_subgroup';
    if (this.isEditMode) return 'fee.edit_group';
    return 'fee.setup_group';
  }

  getSubgroupControl(index: number, field: string): AbstractControl {
    return this.subgroups.at(index).get(field)!;
  }

  getSubgroupGroup(index: number): FormGroup {
    return this.subgroups.at(index) as FormGroup;
  }

  getSubgroupMinDate(index: number): Date {
    return this.subgroupMetas[index]?.minDate ?? minSelectableStartDate();
  }

  getSubgroupSingleMinDate(index: number): Date {
    return this.subgroupMetas[index]?.extendOnlyFloor ?? this.getSubgroupMinDate(index);
  }

  isDailySinglePay(index: number): boolean {
    return isDailySinglePayFrequency(this.getSubgroupControl(index, 'frequency').value);
  }

  addSubgroup(existing?: SubgroupResponse): void {
    this.subgroups.push(this.fb.group({
      id:          [existing?.id ?? null],
      name:        ['', [Validators.required, Validators.maxLength(25)]],
      price:       ['', Validators.required],
      allowCustomAmount: [existing?.allowCustomAmount ?? false],
      mentorName:  [''],
      mentorPhone: ['', Validators.pattern(/^[0-9+\-\s().]*$/)],
      mentorEmail: ['', Validators.email],
      startDate:   [null, Validators.required],
      endDate:     [null],
      frequency:   ['', Validators.required],
      paymentFlow: ['', Validators.required]
    }));
    const idx = this.subgroups.length - 1;
    if (existing) {
      this.subgroups.at(idx).patchValue({
        id: existing.id,
        name: existing.name || '',
        price: existing.price ?? '',
        allowCustomAmount: !!existing.allowCustomAmount,
        mentorName: existing.mentorName || '',
        mentorPhone: existing.mentorPhone || '',
        mentorEmail: existing.mentorEmail || '',
        startDate: toFormDateValue(existing.startDate) ?? null,
        endDate: toFormDateValue(existing.endDate) ?? null,
        frequency: existing.frequency || '',
        paymentFlow: existing.paymentFlow || DEFAULT_PAYMENT_FLOW
      }, { emitEvent: false });
      this.subgroups.at(idx).get('frequency')!.disable({ emitEvent: false });
      this.subgroupMetas.push({
        saved: true,
        expanded: false,
        minDate: minSelectableStartDate(existing.startDate),
        memberCount: existing.memberCount ?? 0,
        extendOnlyFloor: existing.hasOverpayment ? startOfDay(existing.endDate ?? existing.startDate) : null
      });
    } else {
      this.subgroupMetas.push({
        saved: false,
        expanded: true,
        minDate: minSelectableStartDate(),
        memberCount: 0,
        extendOnlyFloor: null
      });
    }
    this.wireSubgroupDailySinglePaySync(this.getSubgroupGroup(idx));
    this.updateSubgroupEndDateValidators(this.getSubgroupGroup(idx));
  }

  private wireSubgroupDailySinglePaySync(group: FormGroup): void {
    group.get('frequency')!.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateSubgroupEndDateValidators(group);
      this.syncDailySinglePayEndDate(group);
    });
    group.get('startDate')!.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.syncDailySinglePayEndDate(group));
    this.syncDailySinglePayEndDate(group);
  }

  private updateSubgroupEndDateValidators(group: FormGroup): void {
    const endCtrl = group.get('endDate')!;
    const validators: ValidatorFn[] = isDailySinglePayFrequency(group.get('frequency')!.value) ? [] : [Validators.required];
    const floor = this.getMetaForGroup(group)?.extendOnlyFloor ?? null;
    if (floor) {
      validators.push(extendOnlyEndDateValidator(floor));
    }
    endCtrl.setValidators(validators);
    endCtrl.updateValueAndValidity({ emitEvent: false });
  }

  private getMetaForGroup(group: FormGroup): SubgroupMeta | undefined {
    const idx = this.subgroups.controls.indexOf(group);
    return idx >= 0 ? this.subgroupMetas[idx] : undefined;
  }

  private showOverpaymentExtendOnlyErrorIfAny(): boolean {
    const hasViolation = this.subgroups.controls.some(
      ctrl => ctrl.get('endDate')!.hasError(OVERPAYMENT_EXTEND_ONLY_ERROR)
    );
    if (!hasViolation) {
      return false;
    }
    this.snackBar.openSnackBar({
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: SnackBarUtil.CLASSES.error,
      data: MockupDataModel.error(this.translate.instant('fee.validation.overpayment_extend_only'))
    });
    return true;
  }

  private syncDailySinglePayEndDate(group: FormGroup): void {
    if (!isDailySinglePayFrequency(group.get('frequency')!.value)) {
      return;
    }
    const start = group.get('startDate')!.value;
    if (start) {
      group.get('endDate')!.setValue(start, { emitEvent: false });
    }
  }

  private mapSubgroupPayload(v: Record<string, unknown>): SubgroupCreateRequest {
    const frequency = v['frequency'] as Frequency;
    return {
      name: v['name'] as string,
      price: Number(v['price']),
      allowCustomAmount: !!v['allowCustomAmount'],
      mentorName: (v['mentorName'] as string) || null,
      mentorPhone: (v['mentorPhone'] as string) || null,
      mentorEmail: (v['mentorEmail'] as string) || null,
      startDate: toApiDateString(v['startDate'])!,
      endDate: resolveSubgroupEndDate(frequency, v['startDate'], v['endDate']),
      frequency,
      paymentFlow: (v['paymentFlow'] as PaymentFlow) || DEFAULT_PAYMENT_FLOW
    };
  }

  private mapCreateSubgroupRequest(v: Record<string, unknown>): CreateSubgroupRequest {
    const frequency = v['frequency'] as Frequency;
    const endDate = resolveSubgroupEndDate(frequency, v['startDate'], v['endDate']);
    return {
      name: v['name'] as string,
      price: Number(v['price']),
      allowCustomAmount: !!v['allowCustomAmount'],
      startDate: toApiDateString(v['startDate'])!,
      frequency,
      paymentFlow: (v['paymentFlow'] as PaymentFlow) || DEFAULT_PAYMENT_FLOW,
      ...(v['mentorName'] ? { mentorName: v['mentorName'] as string } : {}),
      ...(v['mentorPhone'] ? { mentorPhone: v['mentorPhone'] as string } : {}),
      ...(v['mentorEmail'] ? { mentorEmail: v['mentorEmail'] as string } : {}),
      ...(endDate ? { endDate } : {})
    };
  }

  removeSubgroup(index: number): void {
    const subgroupId = this.getSubgroupControl(index, 'id').value as number | null;
    if (this.isEditMode && subgroupId) {
      const config = new MatDialogConfig<SubgroupDeleteDialogData>();
      config.data = {
        subgroupId,
        subgroupName: (this.getSubgroupControl(index, 'name').value as string) || '',
        hasData: (this.subgroupMetas[index]?.memberCount ?? 0) > 0
      };
      config.width = '420px';
      this.dialog.open(SubgroupDeleteDialogComponent, config)
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe((deleted: boolean | undefined) => {
          if (deleted) {
            this.spliceSubgroup(index);
            this.hasUnsavedChanges = true;
          }
        });
      return;
    }
    this.spliceSubgroup(index);
  }

  private spliceSubgroup(index: number): void {
    this.subgroups.removeAt(index);
    this.subgroupMetas = this.subgroupMetas.filter((_, i) => i !== index);
    this.ensureSubgroupDraftForm();
  }

  private ensureSubgroupDraftForm(): void {
    if (this.isEditMode || this.subgroups.length > 0) {
      return;
    }
    this.addSubgroup();
  }

  toggleSubgroup(index: number): void {
    const meta = this.subgroupMetas[index];
    if (meta.expanded) {
      const ctrl = this.subgroups.at(index);
      ctrl.markAllAsTouched();
      if (!ctrl.invalid) {
        this.subgroupMetas[index] = { ...meta, saved: true, expanded: false };
        return;
      }
    }
    meta.expanded = !meta.expanded;
  }

  saveSubgroup(index: number): void {
    const ctrl = this.subgroups.at(index);
    ctrl.markAllAsTouched();
    if (ctrl.invalid) return;
    this.subgroupMetas[index] = { ...this.subgroupMetas[index], saved: true, expanded: false };
  }

  save(): void {
    this.groupForm.markAllAsTouched();
    this.markValidSubgroupsAsSaved();
    if (this.showOverpaymentExtendOnlyErrorIfAny()) return;
    if (!this.atLeastOneSubgroupSaved || this.nameCtrl.invalid) return;

    this.saving = true;
    if (this.isEditMode && this.editingGroupId) {
      this.saveEditMode();
      return;
    }

    const savedSubgroups: CreateSubgroupRequest[] = this.subgroups.controls
      .filter((_, i) => this.subgroupMetas[i].saved)
      .map(ctrl => this.mapCreateSubgroupRequest((ctrl as FormGroup).getRawValue()));

    this.feeService.createFeeGroup(this.userIndustryId, {
      name: this.nameCtrl.value,
      subgroups: savedSubgroups
    }).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.hasUnsavedChanges = false;
          this.navigateBackToOverview();
        },
        error: () => { this.saving = false; }
      });
  }

  cancel(): void {
    this.hasUnsavedChanges = false;
    this.navigateBackToOverview();
  }

  private loadGroupForEdit(groupId: number): void {
    this.feeService.getGroupDetail(groupId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((group: GroupDetailResponse) => {
        this.originalGroupName = group.name || '';
        this.groupForm.patchValue({ name: group.name || '' }, { emitEvent: false });
        this.subgroups.clear();
        this.subgroupMetas = [];
        for (const subgroup of group.subgroups || []) {
          this.addSubgroup(subgroup);
        }
        if (this.isSubgroupEditMode && this.editingSubgroupId) {
          this.keepOnlyEditingSubgroup(this.editingSubgroupId);
        }
        if ((group.subgroups || []).length === 0) {
          this.addSubgroup();
        }
        this.hasUnsavedChanges = false;
      });
  }

  private saveEditMode(): void {
    if (!this.editingGroupId) {
      this.saving = false;
      return;
    }

    const ops = [];
    const newName = this.nameCtrl.value;
    if (newName && newName !== this.originalGroupName) {
      ops.push(this.feeService.updateGroupName(this.editingGroupId, newName));
    }

    for (let i = 0; i < this.subgroups.controls.length; i++) {
      if (!this.subgroupMetas[i]?.saved) continue;
      const raw = this.getSubgroupGroup(i).getRawValue();
      const payload = this.mapSubgroupPayload(raw);
      const subgroupId = raw.id as number | null;
      if (subgroupId) {
        ops.push(this.feeService.updateSubgroup(subgroupId, payload));
      } else {
        ops.push(this.feeService.createSubgroup(this.editingGroupId, payload));
      }
    }

    const request$ = ops.length > 0 ? forkJoin(ops) : of([]);
    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.hasUnsavedChanges = false;
        this.navigateBackToOverview();
      },
      error: () => {
        this.saving = false;
      }
    });
  }

  private navigateBackToOverview(): void {
    if (this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
      return;
    }
    this.router.navigate(['/fees', this.userIndustryId]);
  }

  private keepOnlyEditingSubgroup(subgroupId: number): void {
    const targetIndex = this.subgroups.controls.findIndex(ctrl => Number(ctrl.get('id')?.value) === subgroupId);
    if (targetIndex < 0) return;
    const targetValue = (this.subgroups.at(targetIndex) as FormGroup).getRawValue();
    this.subgroups.clear();
    this.subgroupMetas = [];
    this.addSubgroup({
      id: targetValue.id,
      name: targetValue.name,
      price: targetValue.price,
      allowCustomAmount: !!targetValue.allowCustomAmount,
      mentorName: targetValue.mentorName,
      mentorPhone: targetValue.mentorPhone,
      mentorEmail: targetValue.mentorEmail,
      startDate: targetValue.startDate,
      endDate: targetValue.endDate,
      frequency: targetValue.frequency,
      paymentFlow: targetValue.paymentFlow
    } as SubgroupResponse);
  }
}
